import { useMemo, useEffect } from 'react';
import { DadosComparativoDireto } from '@/features/laudo/types/laudo-wizard.types';
import type { DadosCalculados } from './types';
import { calcularValorM2, calcularFAT, calcularValorHomogeneizado } from './utils';

export function useDadosCalculados(
  dados: DadosComparativoDireto[],
  parametrosFatoracao: string[]
) {
  return useMemo<DadosCalculados[]>(() => {
    return dados.map(item => {
      const valorM2 = calcularValorM2(item.valorAnuncio, item.area);
      const fat = calcularFAT(item, parametrosFatoracao);
      // Avaliando não recebe fator de oferta (não é amostra de mercado).
      const ofertaAplicada = item.amostra === 'Avaliando' ? 1.0 : (item.fatorOferta || 1.0);
      const valorHomogeneizado = calcularValorHomogeneizado(valorM2, fat, ofertaAplicada);

      return {
        ...item,
        valorM2,
        fat,
        valorHomogeneizado,
      };
    });
  }, [dados, parametrosFatoracao]);
}

export function useSincronizarDados(
  dadosCalculados: DadosCalculados[],
  dados: DadosComparativoDireto[],
  onUpdate: (dados: DadosComparativoDireto[]) => void
) {
  useEffect(() => {
    const precisaAtualizar = dadosCalculados.some((itemCalculado, index) => {
      const itemOriginal = dados[index];
      if (!itemOriginal) return true; // Se não existia, precisa atualizar

      const valorM2Original = itemOriginal.valorM2 || 0;
      const fatOriginal = itemOriginal.fat || 0;
      const valorHomogeneizadoOriginal = itemOriginal.valorHomogeneizado || 0;

      // Condição 1: Diferença significativa nos valores
      const temDiferenca =
        Math.abs(itemCalculado.valorM2 - valorM2Original) > 0.01 ||
        Math.abs(itemCalculado.fat - fatOriginal) > 0.001 ||
        Math.abs(itemCalculado.valorHomogeneizado - valorHomogeneizadoOriginal) > 0.01;

      // Condição 2: Valores zerados indevidamente (calculado > 0 mas original = 0)
      const temZeradosIndevidamente =
        (itemCalculado.valorM2 > 0 && valorM2Original === 0) ||
        (itemCalculado.fat > 0 && fatOriginal === 0) ||
        (itemCalculado.valorHomogeneizado > 0 && valorHomogeneizadoOriginal === 0);

      return temDiferenca || temZeradosIndevidamente;
    });

    if (precisaAtualizar) {
      onUpdate(dadosCalculados);
    }
  }, [dadosCalculados, dados, onUpdate]);
}

export function useMediaValorHomogeneizado(dadosCalculados: DadosCalculados[]) {
  return useMemo(() => {
    const dadosComparacao = dadosCalculados.filter(item => item.amostra !== 'Avaliando');
    if (dadosComparacao.length === 0) return 0;
    const soma = dadosComparacao.reduce((acc, item) => acc + item.valorHomogeneizado, 0);
    return soma / dadosComparacao.length;
  }, [dadosCalculados]);
}

export function useMediaValorM2(dadosCalculados: DadosCalculados[]) {
  return useMemo(() => {
    const dadosComparacao = dadosCalculados.filter(item => item.amostra !== 'Avaliando');
    if (dadosComparacao.length === 0) return 0;
    const soma = dadosComparacao.reduce((acc, item) => acc + item.valorM2, 0);
    return soma / dadosComparacao.length;
  }, [dadosCalculados]);
}
